/* eslint-disable newline-after-var */

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FormContextProvider } from '@sw-sw/lib-form';
import { IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import clientApi from '../../../utils/api/client';
import ClientGroupModal from './ClientGroupModal';
import CustomDocTypesSection from './CustomDocTypesSection';

interface ClientDocumentGroupsProps {
  clientId: number;
}

const ClientDocumentGroups: React.FC<ClientDocumentGroupsProps> = ({ clientId }) => {
  const [clientGroups, setClientGroups] = useState<any[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [customDocTypes, setCustomDocTypes] = useState<any[]>([]);
  const mountedRef = useRef(true);
  

  const fetchClientDocumentGroups = async () => {
    try {
      const response = await clientApi.documentGroups.index(clientId);
      if (response) {

        for (const group of response) {

         const filteredDocTypes = group.client_group_items.filter(
      (e:any) =>
        e.doc_type.name !== 'Fixed Details' &&
        e.doc_type.name !== 'Fixed Details Project' &&
        e.doc_type.name !== 'Active Site Maps' &&
        e.is_checked,
    );

    group.client_group_items = filteredDocTypes;

  }
        if (mountedRef.current) setClientGroups(response);
      }
    } catch (error) {
      console.error('Error fetching client document groups:', error);
      toast.error('Failed to load document groups');
    }
  };

    const fetchCustomDocTypes = async () => {
      try {
        const response = await clientApi.getClientsDocTypes.index(clientId);
  
        if (response) {
  
       const filteredDocTypes = response.filter(
      (e) =>
        e.name !== 'Fixed Details' &&
        e.name !== 'Fixed Details Project' &&
        e.name !== 'Active Site Maps'
    );
    
          if (mountedRef.current) setCustomDocTypes(filteredDocTypes);
        }
      } catch (error) {

        console.error('Error fetching custom doc types:', error);
        toast.error('Failed to load custom document types');
        if (mountedRef.current) setCustomDocTypes([]);
      }
    };
  

  useEffect(() => {
    mountedRef.current = true;
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchClientDocumentGroups(),
      ]);
      if (mountedRef.current) setLoading(false);
    };
    fetchData();
    return () => { mountedRef.current = false; };
  }, [clientId]);

  const handleEditGroup = (group: any) => {
    if (group.locked_doc_type_group_id) {
      toast.warning('This group is locked and cannot be edited');
      return;
    }
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedGroup(null);
  };

  const handleUpdateOrder = async (groupId: number, folders: { doc_type_id: number; sort_order: number }[]) => {
    try {
      await clientApi.documentGroups.update(clientId, groupId, folders);
      await fetchClientDocumentGroups();
      toast.success('Document group updated successfully');
      handleCloseModal();
    } catch (error) {
      console.error('Error updating document group:', error);
      toast.error('Failed to update document group');
    }
  };

  const handleToggleLock = async (groupId: number, currentLockState: boolean) => {
    try {
      await clientApi.documentGroups.lock(clientId,groupId, { 
        lock: !currentLockState 
      });

      await fetchClientDocumentGroups();
      toast.success(`Document group ${!currentLockState ? 'locked' : 'unlocked'} successfully`);
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Failed to update lock status');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className='client-document-groups'>
      <CustomDocTypesSection clientId={clientId} 
       fetchCustomDocTypes={fetchCustomDocTypes}
        customDocTypes={customDocTypes}

/>
      
      <div className='document-groups-section'>
        <h4>Document Groups</h4>
        <div className='groups-tab'>
          {clientGroups.map((group) => (
            <div key={group.id} className='group-card'>
              <div className='group-header'>
                <h4 className='group-name'>{group.name}</h4>
                <div className='group-actions'>
                  <button
                    className='action-icon edit-icon'
                    onClick={() => handleEditGroup(group)}
                    title={group.locked_doc_type_group_id ? 'Locked - Cannot Edit' : 'Edit'}
                    disabled={group.locked_doc_type_group_id}
                    style={{ 
                      opacity: group.locked_doc_type_group_id ? 0.5 : 1, 
                      cursor: group.locked_doc_type_group_id ? 'not-allowed' : 'pointer' 
                    }}
                  >
                    <i className='fa fa-pencil' aria-hidden='true' />
                  </button>
                  <IconButton
                    size='small'
                    onClick={() => handleToggleLock(group.id, group.locked_doc_type_group_id)}
                    title={group.locked_doc_type_group_id ? 'Unlock' : 'Lock folder group for all projects under this client.'}
                  >
                    {group.locked_doc_type_group_id ? (
                      <LockIcon className='col-danger' fontSize='small' />
                    ) : (
                      <LockOpenIcon className='col-success' fontSize='small' />
                    )}
                  </IconButton>
                </div>
              </div>
              <p className='group-folders'>
                {group.client_group_items
                  ? group.client_group_items
                      .sort((a: any, b: any) => a.sort_order - b.sort_order)
                      .map((folder: any) => folder.doc_type?.name || folder.name)
                      .join(', ')
                  : 'No folders'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {showEditModal && selectedGroup && !selectedGroup.locked_doc_type_group_id && (
        <FormContextProvider>
          <ClientGroupModal
            show={showEditModal}
            isAdd={false}
            group={selectedGroup}
            customDocTypes={customDocTypes}
            onClose={handleCloseModal}
            onSubmit={(folders:any) => handleUpdateOrder(selectedGroup.id, folders)}
          />
        </FormContextProvider>
      )}
    </div>
  );
};

export default ClientDocumentGroups;
